import { useEffect, useMemo, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const WINDOWS = ["Morning (9am-12pm)", "Afternoon (12pm-5pm)", "Evening (5pm-9pm)"];
const EMPTY_PREF = { days: [], window: "" };
const SCHEDULE_EMAIL = "will2536rogers@gmail.com";
const CONFERENCE_URL = "https://freeconferenceusa.com/index.html";

function ScheduleNote() {
  return (
    <div className="br-schedule" data-testid="book-schedule-note">
      <p>
        Email{" "}
        <a href={`mailto:${SCHEDULE_EMAIL}?subject=Schedule%20My%2030-Minute%20Reading`} data-testid="book-schedule-email-link">
          {SCHEDULE_EMAIL}
        </a>{" "}
        to schedule the date and time of your 30-minute reading.
      </p>
      <p className="br-schedule-sub">
        Readings are held live on{" "}
        <a href={CONFERENCE_URL} target="_blank" rel="noopener noreferrer" data-testid="book-conference-link">
          FreeConferenceUSA
        </a>
        .
      </p>
    </div>
  );
}

function useTimezones() {
  return useMemo(() => {
    const local = Intl.DateTimeFormat().resolvedOptions().timeZone || "America/New_York";
    const all = Intl.supportedValuesOf ? Intl.supportedValuesOf("timeZone") : [local];
    return { local, all: all.includes(local) ? all : [local, ...all] };
  }, []);
}

function PreferenceRow({ index, pref, onChange }) {
  const toggleDay = (d) =>
    onChange({ ...pref, days: pref.days.includes(d) ? pref.days.filter((x) => x !== d) : [...pref.days, d] });
  return (
    <div className="br-pref" data-testid={`book-pref-${index}`}>
      <span className="br-pref-label">
        Choice {index + 1}
        {index === 0 ? "" : " (optional)"}
      </span>
      <div className="br-days">
        {DAYS.map((d) => (
          <button
            key={d}
            type="button"
            className={`br-day ${pref.days.includes(d) ? "is-on" : ""}`}
            aria-pressed={pref.days.includes(d)}
            onClick={() => toggleDay(d)}
            data-testid={`book-pref-${index}-day-${d.toLowerCase()}`}
          >
            {d}
          </button>
        ))}
      </div>
      <select
        className="br-select"
        value={pref.window}
        onChange={(e) => onChange({ ...pref, window: e.target.value })}
        data-testid={`book-pref-${index}-window`}
      >
        <option value="">Time window…</option>
        {WINDOWS.map((w) => (
          <option key={w} value={w}>{w}</option>
        ))}
      </select>
    </div>
  );
}

function Confirmation({ email }) {
  return (
    <div className="intake-page" data-testid="book-confirmation">
      <div className="intake-shell">
        <p className="intake-eyebrow">Request Received</p>
        <h1 className="intake-title">Your reading request is in.</h1>
        <p className="intake-lede">
          Dr. Rogers will personally confirm one of your time windows by email at <strong>{email}</strong>.
        </p>
        <ScheduleNote />
        <a href="/" className="intake-cta-primary" data-testid="book-back-home">Return To The Academy →</a>
      </div>
    </div>
  );
}

export default function BookReading() {
  const tz = useTimezones();
  const [form, setForm] = useState({ name: "", email: "", timezone: tz.local, note: "" });
  const [prefs, setPrefs] = useState([EMPTY_PREF, EMPTY_PREF, EMPTY_PREF]);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => { document.title = "Book Your Reading · S.P.A.C.E. Academy"; }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const setPref = (i) => (p) => setPrefs(prefs.map((x, j) => (j === i ? p : x)));

  async function submit(e) {
    e.preventDefault();
    const filled = prefs.filter((p) => p.days.length && p.window);
    if (!prefs[0].days.length || !prefs[0].window) {
      setError("Please pick at least one day and a time window for your first choice.");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await axios.post(`${API}/reading-request`, {
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        timezone: form.timezone,
        preferences: filled,
        note: form.note.trim() || null,
      });
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || "Something went wrong.";
      setError(typeof msg === "string" ? msg : "Please check your details and try again.");
    } finally {
      setSubmitting(false);
    }
  }

  if (done) return <Confirmation email={form.email} />;

  return (
    <div className="intake-page" data-testid="book-reading-page">
      <div className="intake-shell">
        <a href="/" className="br-back" data-testid="book-back-link">← S.P.A.C.E. Academy</a>
        <p className="intake-eyebrow">Private Reading · Dr. Will Rogers · Masterteacher33</p>
        <h1 className="intake-title" data-testid="book-title">
          Book your <span className="gold-text">private 30-minute reading</span>.
        </h1>
        <p className="intake-lede" data-testid="book-lede">
          Your reading is <strong>live</strong> and <strong>all-inclusive</strong> — covering S.P.A.C.E. Identity
          Engineering and S.P.A.C.E. Identity Transformation. Offer me a few windows that work for you, and I will
          personally confirm one.
        </p>
        <ScheduleNote />

        <form className="intake-form" onSubmit={submit} data-testid="book-form">
          <label className="intake-field">
            <span>Full Name</span>
            <input type="text" value={form.name} onChange={set("name")} required minLength={2} maxLength={120}
              placeholder="Firstname Lastname" data-testid="book-name" />
          </label>
          <label className="intake-field">
            <span>Email</span>
            <input type="email" value={form.email} onChange={set("email")} required
              placeholder="you@example.com" data-testid="book-email" />
          </label>
          <label className="intake-field">
            <span>Your Timezone</span>
            <select className="br-select" value={form.timezone} onChange={set("timezone")} required data-testid="book-timezone">
              {tz.all.map((z) => <option key={z} value={z}>{z.replace(/_/g, " ")}</option>)}
            </select>
          </label>

          <div className="intake-field">
            <span>Preferred Days & Time Windows</span>
            {prefs.map((p, i) => <PreferenceRow key={i} index={i} pref={p} onChange={setPref(i)} />)}
          </div>

          <label className="intake-field">
            <span>What feels out of step right now? (optional)</span>
            <textarea className="br-textarea" rows={4} maxLength={2000} value={form.note} onChange={set("note")}
              placeholder="A sentence or two is plenty." data-testid="book-note" />
          </label>

          {error && <div className="intake-error" data-testid="book-error">{error}</div>}

          <button type="submit" className="intake-submit" disabled={submitting} data-testid="book-submit">
            {submitting ? "Sending…" : "Request My Reading"}
          </button>
          <p className="intake-fine">No payment required · Dr. Rogers confirms your time personally by email.</p>
        </form>
      </div>
    </div>
  );
}
